import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';

/**
 * useUserPref — one per-user JSON array in user_column_prefs, keyed by (owner_id, table_key). The storage the Columns
 * menu uses (useColumnPrefs builds on this) and the reports' remembered filters ('report-filters:…').
 *
 * `saved`: null until loaded (or with no owner / key), [] when there is none, else the stored array.
 * `save(array)`: upserts it. Throws on an error AND on 0 rows written — an RLS-blocked write returns no rows and no
 * error, so the absence of an error is not proof it saved.
 *
 * Supabase v2: await + destructure { data, error }. Never `.catch()` directly on the query builder (a thenable).
 */
export function useUserPref(tableKey, ownerId) {
  const [saved, setSaved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setSaved(null);   // a new key's value is unknown until loaded — never show the previous key's
    if (!ownerId || !tableKey) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: err } = await supabase
          .from('user_column_prefs')
          .select('columns')
          .eq('owner_id', ownerId)
          .eq('table_key', tableKey)
          .maybeSingle();
        if (cancelled) return;
        if (err) { setError(err); setSaved([]); }
        else setSaved(Array.isArray(data?.columns) ? data.columns : []);
      } catch (e) {
        if (!cancelled) { setError(e); setSaved([]); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [ownerId, tableKey]);

  const save = useCallback(async (value) => {
    if (!ownerId || !tableKey) return;
    const { data, error: err } = await supabase
      .from('user_column_prefs')
      .upsert(
        { owner_id: ownerId, table_key: tableKey, columns: value, updated_at: new Date().toISOString() },
        { onConflict: 'owner_id,table_key' }
      )
      .select('owner_id');
    if (err) { setError(err); throw err; }
    if (!data || !data.length) { const e = new Error('Not saved (0 rows written).'); setError(e); throw e; }
    setSaved(value);
  }, [ownerId, tableKey]);

  return { saved, save, loading, error };
}
